export type ConnectorStatus = 'stub' | 'configured' | 'connected' | 'error';
export type AuthMethod = 'api_key' | 'oauth2' | 'bearer_token' | 'none';
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ConnectorParam {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description: string;
  example?: string;
}

export interface ConnectorEndpoint {
  name: string;
  method: HttpMethod;
  path: string;
  description: string;
  params: ConnectorParam[];
  responseShape: string;
}

export interface ApiConnector {
  id: string;
  name: string;
  category: 'distribution' | 'metadata' | 'analytics' | 'rights' | 'storage' | 'ai';
  status: ConnectorStatus;
  version: string;
  description: string;
  baseUrl: string;
  auth: {
    method: AuthMethod;
    headerName?: string;
    scopes?: string[];
    docsUrl: string;
  };
  endpoints: ConnectorEndpoint[];
  rateLimits: { requests: number; window: string };
  selfDescription(): string;
}

function createConnector(def: Omit<ApiConnector, 'selfDescription'>): ApiConnector {
  return {
    ...def,
    selfDescription() {
      const endpointList = def.endpoints
        .map((e) => `  ${e.method} ${e.path} - ${e.description}`)
        .join('\n');
      const authInfo =
        def.auth.method === 'none'
          ? 'No auth required'
          : `${def.auth.method}${def.auth.headerName ? ` (${def.auth.headerName})` : ''}`;
      return [
        `[${def.id}] ${def.name} v${def.version}`,
        `Status: ${def.status} | Category: ${def.category}`,
        `Base: ${def.baseUrl}`,
        `Auth: ${authInfo}`,
        `Rate: ${def.rateLimits.requests}/${def.rateLimits.window}`,
        `Endpoints:`,
        endpointList,
        `Docs: ${def.auth.docsUrl}`,
      ].join('\n');
    },
  };
}

export const CONNECTORS: ApiConnector[] = [
  createConnector({
    id: 'spotify-catalog',
    name: 'Spotify for Creators',
    category: 'distribution',
    status: 'stub',
    version: '1.0.0',
    description: 'Deliver and manage releases on Spotify. Query catalog presence, submit metadata corrections, and track delivery status.',
    baseUrl: 'https://api.spotify.com/v1',
    auth: {
      method: 'oauth2',
      scopes: ['catalog:read', 'catalog:write', 'analytics:read'],
      docsUrl: 'https://developer.spotify.com/documentation/web-api',
    },
    endpoints: [
      {
        name: 'search',
        method: 'GET',
        path: '/search',
        description: 'Search Spotify catalog for existing releases by ISRC or title',
        params: [
          { name: 'q', type: 'string', required: true, description: 'Search query (ISRC, title, artist)', example: 'isrc:USRC17607839' },
          { name: 'type', type: 'string', required: true, description: 'Result type', example: 'track' },
          { name: 'limit', type: 'number', required: false, description: 'Max results (1-50)', example: '20' },
        ],
        responseShape: '{ tracks: { items: [{ id, name, artists, album, external_ids }] } }',
      },
      {
        name: 'get-track',
        method: 'GET',
        path: '/tracks/{id}',
        description: 'Get full track metadata by Spotify ID',
        params: [
          { name: 'id', type: 'string', required: true, description: 'Spotify track ID', example: '6rqhFgbbKwnb9MLmUQDhG6' },
        ],
        responseShape: '{ id, name, artists, album, duration_ms, explicit, isrc, preview_url }',
      },
    ],
    rateLimits: { requests: 100, window: '30s' },
  }),

  createConnector({
    id: 'apple-music-api',
    name: 'Apple Music API',
    category: 'distribution',
    status: 'stub',
    version: '1.0.0',
    description: 'Query Apple Music catalog and manage content delivery through Apple Music for Artists.',
    baseUrl: 'https://api.music.apple.com/v1',
    auth: {
      method: 'bearer_token',
      headerName: 'Music-User-Token',
      docsUrl: 'https://developer.apple.com/documentation/applemusicapi',
    },
    endpoints: [
      {
        name: 'search',
        method: 'GET',
        path: '/catalog/{storefront}/search',
        description: 'Search Apple Music catalog',
        params: [
          { name: 'storefront', type: 'string', required: true, description: 'ISO country code', example: 'us' },
          { name: 'term', type: 'string', required: true, description: 'Search term', example: 'Daft Punk' },
          { name: 'types', type: 'string', required: false, description: 'Resource types to include', example: 'songs,albums' },
        ],
        responseShape: '{ results: { songs: { data: [{ id, attributes: { name, artistName, albumName, isrc } }] } } }',
      },
    ],
    rateLimits: { requests: 60, window: '60s' },
  }),

  createConnector({
    id: 'musicbrainz',
    name: 'MusicBrainz',
    category: 'metadata',
    status: 'stub',
    version: '2.0.0',
    description: 'Open music metadata database. Lookup recordings, releases, and artists with full relational metadata, ISRCs, and ISWCs.',
    baseUrl: 'https://musicbrainz.org/ws/2',
    auth: {
      method: 'none',
      docsUrl: 'https://musicbrainz.org/doc/MusicBrainz_API',
    },
    endpoints: [
      {
        name: 'lookup-recording',
        method: 'GET',
        path: '/recording/{mbid}',
        description: 'Get recording by MusicBrainz ID with relations',
        params: [
          { name: 'mbid', type: 'string', required: true, description: 'MusicBrainz recording ID', example: 'b10bbbfc-cf9e-42e0-be17-e2c3e1d2600d' },
          { name: 'inc', type: 'string', required: false, description: 'Subqueries to include', example: 'artists+releases+isrcs' },
        ],
        responseShape: '{ id, title, length, artist-credit, releases, isrcs }',
      },
      {
        name: 'search-recording',
        method: 'GET',
        path: '/recording',
        description: 'Search recordings by query syntax',
        params: [
          { name: 'query', type: 'string', required: true, description: 'Lucene query', example: 'recording:"Bohemian Rhapsody" AND artist:"Queen"' },
          { name: 'limit', type: 'number', required: false, description: 'Max results', example: '25' },
        ],
        responseShape: '{ recordings: [{ id, title, length, artist-credit, releases }] }',
      },
    ],
    rateLimits: { requests: 1, window: '1s' },
  }),

  createConnector({
    id: 'isrc-lookup',
    name: 'IFPI ISRC Registry',
    category: 'metadata',
    status: 'stub',
    version: '1.0.0',
    description: 'Validate and resolve International Standard Recording Codes. Verify ownership and detect conflicts.',
    baseUrl: 'https://isrc.ifpi.org/api/v1',
    auth: {
      method: 'api_key',
      headerName: 'X-ISRC-Key',
      docsUrl: 'https://isrc.ifpi.org/en/isrc/api-documentation',
    },
    endpoints: [
      {
        name: 'validate',
        method: 'GET',
        path: '/isrc/{code}',
        description: 'Validate ISRC and return registered metadata',
        params: [
          { name: 'code', type: 'string', required: true, description: 'ISRC code (12 chars)', example: 'USRC17607839' },
        ],
        responseShape: '{ valid, code, title, artist, year, registrant, territory }',
      },
    ],
    rateLimits: { requests: 30, window: '60s' },
  }),

  createConnector({
    id: 'soundcloud-api',
    name: 'SoundCloud API',
    category: 'distribution',
    status: 'stub',
    version: '2.0.0',
    description: 'Upload, manage, and query tracks on SoundCloud. Supports metadata sync and playlist management.',
    baseUrl: 'https://api.soundcloud.com',
    auth: {
      method: 'oauth2',
      scopes: ['non-expiring', 'upload'],
      docsUrl: 'https://developers.soundcloud.com/docs/api/reference',
    },
    endpoints: [
      {
        name: 'upload',
        method: 'POST',
        path: '/tracks',
        description: 'Upload a new track',
        params: [
          { name: 'title', type: 'string', required: true, description: 'Track title' },
          { name: 'asset_data', type: 'object', required: true, description: 'Audio file binary' },
          { name: 'sharing', type: 'string', required: false, description: 'public or private', example: 'private' },
        ],
        responseShape: '{ id, permalink_url, title, duration, state }',
      },
      {
        name: 'get-track',
        method: 'GET',
        path: '/tracks/{id}',
        description: 'Get track metadata',
        params: [
          { name: 'id', type: 'number', required: true, description: 'SoundCloud track ID', example: '123456789' },
        ],
        responseShape: '{ id, title, user, duration, playback_count, download_url }',
      },
    ],
    rateLimits: { requests: 100, window: '60s' },
  }),

  createConnector({
    id: 'audiblemagic',
    name: 'Audible Magic',
    category: 'rights',
    status: 'stub',
    version: '3.1.0',
    description: 'Content identification and rights management. Fingerprint audio, detect unauthorized use, and manage ownership claims.',
    baseUrl: 'https://api.audiblemagic.com/v3',
    auth: {
      method: 'api_key',
      headerName: 'Authorization',
      docsUrl: 'https://www.audiblemagic.com/developers',
    },
    endpoints: [
      {
        name: 'identify',
        method: 'POST',
        path: '/identify',
        description: 'Submit audio fingerprint for identification',
        params: [
          { name: 'audio', type: 'object', required: true, description: 'Audio data or URL for fingerprinting' },
          { name: 'callback_url', type: 'string', required: false, description: 'Webhook for async results' },
        ],
        responseShape: '{ match, confidence, asset: { title, owner, isrc, territory_rights } }',
      },
      {
        name: 'register',
        method: 'POST',
        path: '/assets',
        description: 'Register new audio asset for rights tracking',
        params: [
          { name: 'title', type: 'string', required: true, description: 'Asset title' },
          { name: 'isrc', type: 'string', required: false, description: 'ISRC code' },
          { name: 'audio_url', type: 'string', required: true, description: 'URL to audio file' },
        ],
        responseShape: '{ asset_id, fingerprint_id, status, registered_at }',
      },
    ],
    rateLimits: { requests: 10, window: '60s' },
  }),

  createConnector({
    id: 'cloudinary-audio',
    name: 'Cloudinary (Audio)',
    category: 'storage',
    status: 'stub',
    version: '1.0.0',
    description: 'Audio asset storage, transcoding, and CDN delivery. Manages masters, stems, and proxy files.',
    baseUrl: 'https://api.cloudinary.com/v1_1/{cloud_name}',
    auth: {
      method: 'api_key',
      headerName: 'Authorization',
      docsUrl: 'https://cloudinary.com/documentation/upload_images',
    },
    endpoints: [
      {
        name: 'upload',
        method: 'POST',
        path: '/auto/upload',
        description: 'Upload audio asset with automatic format detection',
        params: [
          { name: 'file', type: 'string', required: true, description: 'File URL or base64 data' },
          { name: 'public_id', type: 'string', required: false, description: 'Custom asset ID' },
          { name: 'resource_type', type: 'string', required: false, description: 'Force resource type', example: 'video' },
        ],
        responseShape: '{ public_id, secure_url, format, duration, bytes, resource_type }',
      },
    ],
    rateLimits: { requests: 500, window: '3600s' },
  }),

  createConnector({
    id: 'openai-whisper',
    name: 'OpenAI Whisper (Transcription)',
    category: 'ai',
    status: 'stub',
    version: '1.0.0',
    description: 'Speech-to-text transcription with word-level timestamps. Used for lyric sync and vocal detection.',
    baseUrl: 'https://api.openai.com/v1',
    auth: {
      method: 'bearer_token',
      headerName: 'Authorization',
      docsUrl: 'https://platform.openai.com/docs/api-reference/audio',
    },
    endpoints: [
      {
        name: 'transcribe',
        method: 'POST',
        path: '/audio/transcriptions',
        description: 'Transcribe audio file to text with timestamps',
        params: [
          { name: 'file', type: 'object', required: true, description: 'Audio file (mp3, wav, m4a, etc.)' },
          { name: 'model', type: 'string', required: true, description: 'Model ID', example: 'whisper-1' },
          { name: 'timestamp_granularities', type: 'array', required: false, description: 'Granularity levels', example: '["word","segment"]' },
          { name: 'response_format', type: 'string', required: false, description: 'Output format', example: 'verbose_json' },
        ],
        responseShape: '{ text, words: [{ word, start, end }], segments: [{ text, start, end }] }',
      },
    ],
    rateLimits: { requests: 50, window: '60s' },
  }),
];

export function getConnectorsByCategory(category: ApiConnector['category']): ApiConnector[] {
  return CONNECTORS.filter((c) => c.category === category);
}

export function getConnectorById(id: string): ApiConnector | undefined {
  return CONNECTORS.find((c) => c.id === id);
}

export function discoverAll(): string {
  const grouped: Record<string, ApiConnector[]> = {};
  for (const c of CONNECTORS) {
    if (!grouped[c.category]) grouped[c.category] = [];
    grouped[c.category].push(c);
  }
  const lines: string[] = ['=== API Connectors Registry ===', ''];
  for (const [cat, conns] of Object.entries(grouped)) {
    lines.push(`[${cat.toUpperCase()}]`);
    for (const c of conns) {
      lines.push(`  ${c.id} (${c.status}) - ${c.name}`);
    }
    lines.push('');
  }
  lines.push(`Total: ${CONNECTORS.length} connectors | Use "describe <id>" for details`);
  return lines.join('\n');
}

export function describeConnector(id: string): string {
  const c = getConnectorById(id);
  if (!c) return `Unknown connector: "${id}". Use "discover" to list available connectors.`;
  return c.selfDescription();
}
